import { makeAssistantToolUI } from '@assistant-ui/react'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { HybridSearchView } from '@/components/agent/tools/HybridSearchView'
import { useDocumentOverlayOptional } from '@/components/document-overlay/DocumentDetailOverlayContext'
import {
  type RowsTarget,
  StructuredRowsDialog,
} from '@/features/structured-ingest/StructuredRowsDialog'
import {
  conversationLink,
  type HybridSearchArgs,
  type HybridSearchHit,
  type HybridSearchResult,
  parseResult,
  recordingLink,
  rowWindowTarget,
} from './hybrid-search'

type Status = { type: 'running' | 'complete' | 'incomplete' | string }

interface HybridSearchRenderProps {
  args: HybridSearchArgs
  result?: HybridSearchResult | string
  status: Status
}

/**
 * Connected `hybrid_search` (search_documents) tool card: parses the backend
 * payload, wires the per-hit "open document" action to the document overlay,
 * names the recording page a passage of a HUMINT recording is heard on, and
 * hands pure data to HybridSearchView.
 */
function HybridSearchRender({ args, result, status }: HybridSearchRenderProps) {
  const overlay = useDocumentOverlayOptional()
  const navigate = useNavigate()
  const { caseId } = useParams<{ caseId: string }>()

  const running = status.type === 'running'
  const parsed = useMemo(() => parseResult(result), [result])
  const errored = parsed?.status === 'error' || status.type === 'incomplete'

  // Only expose the open-document handler when the overlay context is present.
  // The view then renders each hit as a button (vs. static text) and decides
  // per-row whether a `document_id` is available to open.
  const onOpenDocument = overlay ? (documentId: string) => overlay.open(documentId) : undefined
  // A scene, an appearance or a plate found in a recording opens the recording's player at
  // the moment it was seen and runs to the end of its window (PRD-0074 slice 08).
  const onOpenAt = overlay
    ? (hit: HybridSearchHit) =>
        overlay.open({
          documentId: hit.document_id,
          atSeconds: hit.start_seconds ?? null,
          untilSeconds: hit.end_seconds ?? null,
        })
    : undefined

  // A conversation-window hit is self-anchoring (ADR-0010), so it opens the
  // Mobile Forensics reader on that thread at the window's start rather than
  // the document overlay — the evidence behind it is the exchange, not a file.
  const onOpenConversation = caseId
    ? (hit: HybridSearchHit) => {
        const to = conversationLink(caseId, hit)
        if (to) navigate(to)
      }
    : undefined

  // A passage of a HUMINT recording is heard on the recording's page, at the
  // passage's start. The view gets the address, not an action: the row is a
  // link, so the recording also opens beside the chat. A passage of an uploaded
  // audio file has no such page and opens in the document overlay.
  const recordingHref = caseId ? (hit: HybridSearchHit) => recordingLink(caseId, hit) : undefined

  // A row-window hit (structured ingest) carries its table and row numbers, so
  // it opens those verbatim rows in place — the evidence is the rows themselves.
  const [rowsTarget, setRowsTarget] = useState<RowsTarget | null>(null)
  const onOpenRows = caseId
    ? (hit: HybridSearchHit) => {
        const target = rowWindowTarget(hit)
        if (!target) return
        const first = target.positions[0]
        const last = target.positions[target.positions.length - 1]
        setRowsTarget({
          analysisId: target.analysisId,
          tableId: target.tableId,
          query: { positions: target.positions },
          title: hit.document_name || 'Source rows',
          subtitle:
            first !== undefined && last !== undefined ? `rows ${first + 1}–${last + 1}` : null,
          highlight: parsed?.query ?? args.query,
        })
      }
    : undefined

  return (
    <>
      <HybridSearchView
        args={args}
        parsed={parsed}
        running={running}
        errored={errored}
        onOpenDocument={onOpenDocument}
        onOpenAt={onOpenAt}
        onOpenConversation={onOpenConversation}
        onOpenRows={onOpenRows}
        recordingHref={recordingHref}
      />
      {caseId && (
        <StructuredRowsDialog
          caseId={caseId}
          target={rowsTarget}
          onClose={() => setRowsTarget(null)}
        />
      )}
    </>
  )
}

export const HybridSearchToolUI = makeAssistantToolUI<
  HybridSearchArgs,
  HybridSearchResult | string
>({
  toolName: 'hybrid_search',
  render: HybridSearchRender,
})
